import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Leftsidebar from "../../sidebar/leftsidebar";
import LeftResponsivesidebar from "../../sidebar/left-responsive";
import Headmenu from "../../sidebar/headmenu";
import availableBalanceIcon from "../../../assets/images/icons/billing/available-balance.svg";
import currentSpendIcon from "../../../assets/images/icons/billing/current-spend.svg";
import accountCapIcon from "../../../assets/images/icons/billing/account-cap.svg";
import prepaidCreditsIcon from "../../../assets/images/icons/billing/prepaid-credits.svg";
import nextPaymentDueIcon from "../../../assets/images/icons/billing/next-payment-due.svg";
import paymentStatusIcon from "../../../assets/images/icons/billing/payment-status.svg";
import taxProfileStatusIcon from "../../../assets/images/icons/billing/tax-profile-status.svg";
import checkCircleIcon from "../../../assets/images/icons/billing/check-circle.svg";
import arrowIcon from "../../../assets/images/icons/billing/arrow-left.svg";
import { getBillingProfile, getTaxProfile, getSpendSummary } from "../../../hook/useAdsApi";

const BILLING_MODEL_LABELS = { prepaid: "Prepaid Credits", postpaid: "Post Paid Credits" };
const PAYMENT_STATUS_LABELS = { none: "Not set", succeeded: "Active", failed: "Failed" };

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "numeric" }) : null;

function formatPaymentMethod(paymentMethod) {
  if (!paymentMethod) return "Not added yet";
  if (paymentMethod.type === "upi") return paymentMethod.vpa_masked || "UPI";
  return `${paymentMethod.brand || "Card"} •••• ${paymentMethod.last4 || ""}`;
}

// "X day(s) Y hour(s)" remaining until due_date, or "overdue by X day(s)" once it's passed - the
// dashboard's own reload lazily flips status to suspended in that case (getBillingProfile calls
// syncPostpaidBillingStatus), so this is just for display, not what decides active/blocked.
function formatDueIn(dueDate) {
  if (!dueDate) return null;
  const diffMs = new Date(dueDate).getTime() - Date.now();
  const totalHours = Math.floor(Math.abs(diffMs) / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const span = days > 0 ? `${days} day${days !== 1 ? "s" : ""} ${hours} hour${hours !== 1 ? "s" : ""}` : `${hours} hour${hours !== 1 ? "s" : ""}`;
  return diffMs > 0 ? `Due in ${span}` : `Overdue by ${span}`;
}

export default function AdBillingDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [taxProfile, setTaxProfile] = useState(null);
  const [spendSummary, setSpendSummary] = useState(null);

  const refreshProfile = () =>
    getBillingProfile()
      .then((res) => setProfile(res))
      .catch(() => setProfile({ configured: false }));

  useEffect(() => {
    Promise.all([
      refreshProfile(),
      getTaxProfile().then(setTaxProfile).catch(() => {}),
      getSpendSummary().then(setSpendSummary).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="dashboard w-full min-h-screen flex">
        <Leftsidebar />
        <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
          <LeftResponsivesidebar />
          <Headmenu />
          <p className="text-sm text-gray-500">Loading billing details...</p>
        </main>
      </div>
    );
  }

  const configured = Boolean(profile?.configured);

  // Rest of the dashboard renders normally even before a profile exists - only the first
  // mini-card (Billing Model) swaps to a "Set Billing Model" CTA until one is configured.
  const summary = {
    availableBalance: Number(profile?.credit_balance || 0),
    billingModel: BILLING_MODEL_LABELS[profile?.billing_model] || "Not set",
    defaultPaymentMethod: formatPaymentMethod(profile?.payment_method),
    paymentStatus: PAYMENT_STATUS_LABELS[profile?.last_payment_status] || "Not set",
    nextPaymentDue: formatDate(profile?.postpaid_next_cycle_due_at),
    currentSpend: Number(spendSummary?.current_spend || 0),
    accountCap: spendSummary?.cap !== null && spendSummary?.cap !== undefined ? Number(spendSummary.cap) : null,
    taxProfileStatus: taxProfile?.billing_name && taxProfile?.tax_country ? "Set" : "Not set",
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-5xl">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Billing &amp; Payments</h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage your advertising spend, payment method and billing information
              </p>
              <div className="border-b border-gray-200 mt-2 w-full max-w-[420px]" />
            </div>

            {/* Postpaid stakeholder decision: no card is saved/auto-charged, so there's no
                "auto-charge failed" to fix here - just a bill to pay. Reads the unpaid invoice
                getBillingProfile returns (only set while status is payment_grace/suspended). */}
            {profile?.unpaid_invoice && (
              <div className="rounded-2xl border-[1.5px] border-[#e6a4a4] bg-[#fdeaea] p-4 mb-6">
                <div className="flex items-start gap-3">
                  <span className="text-2xl leading-none">⚠️</span>
                  <div className="flex-1">
                    <h3 className="font-bold text-sm text-[#a33]">
                      {profile.status === "suspended" ? "Campaigns Paused - Invoice Overdue" : "Invoice Payment Due"}
                    </h3>
                    <p className="text-sm text-gray-700 mt-1">
                      Invoice #{profile.unpaid_invoice.invoice_number} for{" "}
                      <span className="font-semibold">{profile.currency_code || "$"} {Number(profile.unpaid_invoice.total_amount).toFixed(2)}</span>{" "}
                      is awaiting payment.
                    </p>
                    {formatDueIn(profile.unpaid_invoice.due_date) && (
                      <p className="text-xs font-semibold text-[#a33] mt-1">
                        {formatDueIn(profile.unpaid_invoice.due_date)}
                      </p>
                    )}
                    <div className="flex gap-3 mt-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/ads/billing/invoices/${profile.unpaid_invoice.id}/pay`)}
                        className="text-sm font-semibold bg-[#a33] text-white rounded-full px-4 py-1.5"
                      >
                        Pay Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="rounded-[32px] py-4 px-6 bg-[#fefdeb] flex items-center gap-3">
                <img src={availableBalanceIcon} alt="" className="w-12 h-12" />
                <div className="flex flex-col">
                  <span className="text-[#00092c] font-black text-[28px]">
                    {profile?.currency_code || "$"} {summary.availableBalance.toLocaleString()}
                  </span>
                  <span className="text-gray-600 font-semibold text-sm">Available Balance</span>
                </div>
              </div>
              <div className="rounded-[32px] py-4 px-6 bg-[#ebf8ed] flex items-center gap-3">
                <img src={currentSpendIcon} alt="" className="w-12 h-12" />
                <div className="flex flex-col">
                  <span className="text-[#00092c] font-black text-[28px]">
                    {profile?.currency_code || "$"} {summary.currentSpend.toLocaleString()}
                  </span>
                  <span className="text-[#095228] font-semibold text-sm">Current Spend</span>
                </div>
              </div>
              <div className="rounded-[32px] py-4 px-6 bg-[#fdeaea] flex items-center gap-3">
                <img src={accountCapIcon} alt="" className="w-12 h-12" />
                <div className="flex flex-col">
                  <span className="text-[#00092c] font-black text-[28px]">
                    {summary.accountCap !== null
                      ? `${profile?.currency_code || "$"} ${summary.accountCap.toLocaleString()}`
                      : "No cap set"}
                  </span>
                  <span className="text-[#a33] font-semibold text-sm">Account Cap</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {configured ? (
                <div className="rounded-2xl border-[1.5px] border-[#39B54A] p-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <img src={prepaidCreditsIcon} alt="" className="w-5 h-5" />
                    <button
                      type="button"
                      onClick={() => navigate("/ads/billing/model")}
                      className="text-xs text-[#0052FF] italic underline"
                    >
                      Change
                    </button>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">Billing Model</span>
                  <p className="font-bold text-sm mt-0.5">{summary.billingModel}</p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/ads/billing/model?mode=setup")}
                  className="rounded-2xl border-[1.5px] border-[#39B54A] p-3 text-left bg-[#095228]"
                >
                  <img src={prepaidCreditsIcon} alt="" className="w-5 h-5 mb-1.5 brightness-0 invert" />
                  <span className="text-xs text-white/80 whitespace-nowrap">Billing Model</span>
                  <p className="font-bold text-sm mt-0.5 text-white">Set Billing Model →</p>
                </button>
              )}

              {/* Default Payment Method box hidden for now - AdPaymentMethods.js's "+ Add New
                  Card" button has no real handler yet (same static-placeholder state as
                  everything was before this pass), so a "Change" link into that screen would be
                  misleading. Bring this back once payment-methods add/list is actually wired.
              <div className="rounded-2xl border-[1.5px] border-[#39B54A] p-3">
                <div className="flex items-center justify-between mb-1.5">
                  <img src={defaultPaymentMethodIcon} alt="" className="w-5 h-5" />
                  <button
                    type="button"
                    onClick={() => navigate("/ads/billing/payment-methods")}
                    className="text-xs text-[#0052FF] italic underline"
                  >
                    Change
                  </button>
                </div>
                <span className="text-xs text-gray-500 whitespace-nowrap">Default Payment Method</span>
                <p className="font-bold text-sm mt-0.5">{summary.defaultPaymentMethod}</p>
              </div>
              */}

              {/* Only meaningful for Postpaid + Monthly billing cycle - Prepaid sellers and
                  Postpaid sellers on threshold-only billing have no fixed next-bill date. */}
              <div className="rounded-2xl border-[1.5px] border-[#39B54A] p-3">
                <img src={nextPaymentDueIcon} alt="" className="w-5 h-5 mb-1.5" />
                <span className="text-xs text-gray-500 whitespace-nowrap">Next Payment Due</span>
                <p className={`font-bold text-sm mt-0.5 ${summary.nextPaymentDue ? "" : "text-gray-400"}`}>
                  {summary.nextPaymentDue || "Not available yet"}
                </p>
              </div>

              <div className="rounded-2xl border-[1.5px] border-[#39B54A] p-3">
                <img src={paymentStatusIcon} alt="" className="w-5 h-5 mb-1.5" />
                <span className="text-xs text-gray-500 whitespace-nowrap">Payment Status</span>
                <p className="font-bold text-sm mt-0.5 flex items-center gap-1">
                  <img src={checkCircleIcon} alt="" className="w-3.5 h-3.5" />
                  {summary.paymentStatus}
                </p>
              </div>

              <div className="rounded-2xl border-[1.5px] border-[#39B54A] p-3">
                <img src={taxProfileStatusIcon} alt="" className="w-5 h-5 mb-1.5" />
                <span className="text-xs text-gray-500 whitespace-nowrap">Tax Profile Status</span>
                <p className="font-bold text-sm mt-0.5 flex items-center gap-1">
                  <img src={checkCircleIcon} alt="" className="w-3.5 h-3.5" />
                  {summary.taxProfileStatus}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-200 mb-5" />

            {/* Disabled until a billing model exists - Add Credits hard-fails server-side
                without a profile, and the other three aren't meaningful yet either (no spend to
                cap, no invoices to view, tax profile is fine standalone but there's nothing to
                bill against). Add Credits is also disabled for postpaid - credit_balance is a
                prepaid-only concept (assertCampaignBudgetIsFunded never reads it for postpaid),
                so a postpaid seller topping up here would be really charged via Stripe for a
                balance nothing ever uses. */}
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate("/ads/billing/add-credits")}
                disabled={!configured || profile?.billing_model === "postpaid"}
                title={profile?.billing_model === "postpaid" ? "Add Credits is only for Prepaid billing" : undefined}
                className="bg-[#095228] text-white font-semibold pl-5 pr-2 py-2 rounded-full text-sm hover:opacity-90 transition flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40"
              >
                Add Credits
                <img src={arrowIcon} alt="" className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => navigate("/ads/billing/tax-profile")}
                disabled={!configured}
                className="bg-[#095228] text-white font-semibold pl-5 pr-2 py-2 rounded-full text-sm hover:opacity-90 transition flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40"
              >
                Tax and Billing
                <img src={arrowIcon} alt="" className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => navigate("/ads/billing/invoices")}
                disabled={!configured}
                className="bg-[#095228] text-white font-semibold pl-5 pr-2 py-2 rounded-full text-sm hover:opacity-90 transition flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40"
              >
                View Invoices
                <img src={arrowIcon} alt="" className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => navigate("/ads/billing/spending-controls")}
                disabled={!configured}
                className="bg-[#095228] text-white font-semibold pl-5 pr-2 py-2 rounded-full text-sm hover:opacity-90 transition flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40"
              >
                Spending Controls
                <img src={arrowIcon} alt="" className="w-5 h-5" />
              </button>
            </div>
          </div>
        </main>
      </main>
    </div>
  );
}
